<template>
  <b-modal id="wlan-bcd-request-form" size="xl" @shown="update" @ok="sendWIFIBCDRequest">
    <template #modal-title>
      {{ $t('components.wlan_bcd_request_form.title') }}
    </template>
    <b-alert :show="error_message !== null" variant="danger" dismissible>
      {{ error_message }}
    </b-alert>
    <b-overlay rounded="sm">
      <b-form>
        <b-form-group :label="$t('global.enumeration.1', { msg: $tc('system.organizational_unit', 1) })" label-for="oe-select">
          <Typeahead :data="oes" v-model="oe_search"
                     :placeholder="$tc('system.organizational_unit', 1)"
                     @select="selected_oe=$event;$nextTick(updateGroups)"
                     @input="selected_oe=null;updateGroups()"
                     :serializer="serialize_oe"
                     :min-matching-chars="-1"
                     :max-matches="-1"
                     :disabled="(oes && oes.length === 0) || loading_edit_action"
                     compact
                     ref="oe-select"
                     class="mb-3" id="oe-select"/>
        </b-form-group>
        <b-form-group v-if="selected_oe"
                      label-for="group-select" :description="$t('components.bcd_request_form.group_assignment_note')">
          <template #label>
            {{ $t('global.enumeration.2', { msg: $t('system.netvs_group') }) }}
          </template>
          <Typeahead :data="groups" v-model="group_search"
                     :placeholder="$tc('system.group', 1)"
                     @select="selected_group=$event"
                     @input="selected_group=null"
                     :serializer="serialize_group"
                     :min-matching-chars="-1"
                     :max-matches="-1"
                     :disabled="(groups && groups.length === 0) || loading_edit_action"
                     compact
                     ref="group-select"
                     id="group-select"/>
        </b-form-group>
        <b-alert v-if="selected_oe" show variant="info">
          <b-row>
            <b-col cols="auto" class="my-auto">
              <netvs-icon icon="info" size="2x"></netvs-icon>
            </b-col>
            <b-col class="h-100 my-auto">
              <p v-if="!groups || groups.length === 0" class="my-auto">
                {{ $t('components.bcd_request_form.group_assignment_no_group') }}
                <RouterLink to="/cntl/groups" target="_blank">{{ $tc('system.group', 2) }}</RouterLink>
              </p>
              <p v-else class="my-auto">{{ $t('components.bcd_request_form.group_assignment_location') }}
                <RouterLink to="/cntl/groups" target="_blank">{{ $tc('system.group', 2) }}</RouterLink>
              </p>
            </b-col>
          </b-row>
        </b-alert>
        <b-form-group v-if="selected_group"
                      :description="$t('components.requests.bcd.protection_requirements_note')">
          <template #label>
            {{ $t('global.enumeration.3', { msg: $t('components.requests.bcd.protection_requirements') }) }}
            <a v-if="$store.state.locale !== 'de'" href="https://www.scc.kit.edu/en/services/4325.php" target="_blank">
              <netvs-icon icon="info"></netvs-icon>
            </a>
            <a v-else href="https://www.scc.kit.edu/dienste/4325.php" target="_blank">
              <netvs-icon icon="info"></netvs-icon>
            </a>
          </template>
          <b-form-select v-model="protection_requirements_select" :options="translated_protection_requirements">
            <template #first>
              <b-form-select-option :value="null" disabled>{{ $t('system.please_select') }}</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-form-group v-if="selected_group && protection_requirements_select && protection_requirements_select !== 3 && protection_requirements_select !== 4"
                      :label="$t('components.requests.bcd.protection_requirements_explanation')">
          <b-form-textarea :placeholder="$t('components.requests.all.placeholder_text')"
                           v-model="protection_requirements_explanation_text"/>
        </b-form-group>
        <b-form-group v-if="protection_requirements_select && (protection_requirements_select === 3 || protection_requirements_select === 4 || (protection_requirements_explanation_text && protection_requirements_explanation_text.length > 0))"
                      :label="$t('global.enumeration.4', { msg: $t('components.requests.bcd.name_of_bcd') })"
                      :description="$t('components.requests.bcd.name_of_bcd_description')">
          <b-input-group>
            <b-form-input :placeholder="$t('components.requests.bcd.naming_example_placeholder')"
                          :state="checkBCDName"
                          v-model="bcd_name"/>
            <b-input-group-append>
              <b-button v-b-toggle.guide_collapse variant="outline-secondary">
                <netvs-icon icon="info"></netvs-icon>
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <b-collapse id="guide_collapse">
            <div class="mt-2 mb-2">
              <h5>{{ $t('components.requests.bcd.notes_on_naming') }}</h5>
              <p>{{ $t('components.requests.bcd.naming_scheme_usage') }}:
                <code>{{ $t('components.requests.bcd.naming_scheme') }}</code></p>
              <p class="mb-1"><code>{{ $t('components.requests.bcd.naming_oe') }}</code>
                {{ $t('components.requests.bcd.naming_oe_description') }}</p>
              <p class="mb-1"><code>{{ $t('components.requests.bcd.naming_division') }}</code>
                {{ $t('components.requests.bcd.naming_division_description') }}</p>
              <p class="mb-1"><code>{{ $t('components.requests.bcd.naming_function') }}</code>
                {{ $t('components.requests.bcd.naming_function_description') }}</p>
              <p class="mb-1"><code>{{ $t('components.requests.bcd.naming_number') }}</code>
                {{ $t('components.requests.bcd.naming_number_description') }}</p>
              <p>{{ $t('components.requests.bcd.naming_length') }}</p>
            </div>
          </b-collapse>
        </b-form-group>
        <b-form-group v-if="bcd_name && bcd_name.length > 0"
          :label="$t('global.enumeration.5', { msg: $t('components.requests.bcd.access_to_internet') })">
          <b-form-checkbox v-model="access_to_internet_value">
            {{ $t('components.requests.bcd.access_to_internet_text') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="bcd_name && bcd_name.length > 0"
                      :label="$t('global.enumeration.6', { msg: $t('components.requests.wlan.access_methods') })">
          <b-form-radio-group v-model="access_method" stacked>
            <b-form-radio :value="1">
              {{ $t('components.requests.wlan.access_methods_options.wifi') }}
            </b-form-radio>
            <b-form-radio :value="2">
              {{ $t('components.requests.wlan.access_methods_options.iot') }}
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <b-form-group v-if="access_method && access_method === 1"
          :description="$t('components.requests.wlan.permission_group_description')" >
          <template #label>
            {{ $t('global.enumeration.7', { msg: $t('components.requests.wlan.permission_group') }) }}
            <a href="https://www.scc.kit.edu/dienste/gruppenverwaltung.php" target="_blank">
              <netvs-icon icon="info"></netvs-icon>
            </a>
          </template>
          <b-form-input v-model="permission_group" :placeholder="$t('components.requests.all.placeholder_text')" />
        </b-form-group>
        <b-form-group v-if="access_method && (access_method === 2 || (access_method === 1 && permission_group && permission_group.length > 0))"
                      :label="$t('global.enumeration.8', { msg: $t('components.requests.wlan.device_count') })">
          <b-form-input v-model="device_count" type="number" min="1" step="1" max="1024" />
        </b-form-group>
        <b-form-group v-if="access_method && (access_method === 2 || (access_method === 1 && permission_group && permission_group.length > 0))"
                      :label="$t('global.enumeration.9', { msg: $t('components.bcd_request_form.dhcp_own') })">
          <b-form-checkbox
            v-model="dhcp_own_server"
            :disabled="dhcp_wish">
            {{ $t('components.bcd_request_form.dhcp_own_question') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="dhcp_own_server"
          :label="$t('components.bcd_request_form.dhcp_ip_address')">
          <b-form-input v-model="dhcp_own_server_address" :placeholder="$t('components.requests.all.placeholder_text')"/>
        </b-form-group>
        <b-form-group v-if="access_method && (access_method === 2 || (access_method === 1 && permission_group && permission_group.length > 0))"
          :description="$t('components.bcd_request_form.dhcp_wish_note')">
          <template #label>
            {{ $t('global.enumeration.10', { msg: $t('system.dhcp') }) }}
            <a href="https://doku.netdb.scc.kit.edu/dhcp/dnsvs/" target="_blank">
              <netvs-icon icon="info"></netvs-icon>
            </a>
          </template>
          <b-form-checkbox v-model="dhcp_wish" :disabled="dhcp_own_server">
            {{ $t('components.bcd_request_form.dhcp_wish') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="dhcp_wish"
                      :label="$t('global.enumeration.11', { msg: $t('components.bcd_request_form.dhcp_pool') })">
          <b-form-checkbox v-model="dhcp_pool_wish">
            {{ $t('components.bcd_request_form.dhcp_pool_wish') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="dhcp_pool_wish"
                      :label="$t('components.bcd_request_form.dhcp_pool_size')" >
          <b-form-input v-model="dhcp_pool_size" type="number" min="1" step="1" max="1024"/>
        </b-form-group>
        <b-form-group :label="$t('components.requests.all.additional_recipients')"
                      :description="$t('components.requests.all.additional_recipients_description')"
                      label-for="input_additional_recipients">
          <b-form-input
            :placeholder="$t('components.requests.all.additional_recipients')"
            id="input_additional_recipients"
            v-model="additional_recipients"/>
        </b-form-group>
        <b-form-group :label="$t('components.bcd_request_form.additional_comments')">
          <b-form-textarea :placeholder="$t('components.requests.all.placeholder_text')"
                           v-model="additional_comments_text"/>
        </b-form-group>
      </b-form>
    </b-overlay>
    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="reset">
        {{ $t('components.requests.all.reset_request') }}
      </b-button>
      <b-button variant="secondary" @click="cancel()">
        {{ $t('components.requests.all.cancel') }}
      </b-button>
      <b-button variant="primary" :disabled="!access_method || (access_method === 1 && !permission_group)" @click="ok()">
        {{ $t('components.requests.all.send') }}
        <b-spinner small v-show="sending"/>
        <netvs-icon v-show="status_icon !== null" :icon="status_icon"></netvs-icon>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Typeahead from '@/components/Typeahead.vue'
import OUService from '@/api-services/ou.service'
import RequestsService from '@/api-services/requests.service'
import translationutil from '@/util/translationutil'

export default {
  name: 'WLANBCDRequestForm',
  components: { Typeahead },
  data() {
    return {
      error_message: null,
      loading_edit_action: false,
      sending: false,
      status_icon: null,
      additional_recipients: null,
      oes: null,
      oe_search: '',
      selected_oe: null,
      serialize_oe: oe => `${'title' in oe ? translationutil.translate_netdb_ot_lang_attr(oe.title, this.$store.state) : oe.name} (${oe.fq_name})`,
      groups: null,
      group_search: '',
      selected_group: null,
      serialize_group: group => group.name,
      bcd_name: '',
      protection_requirements_select: null,
      protection_requirements_explanation_text: null,
      protection_requirements_options: [
        { value: 1, translation_key: 'components.requests.bcd.protection_requirements_items.extranet_without_firewall' },
        { value: 2, translation_key: 'components.requests.bcd.protection_requirements_items.extranet_with_firewall' },
        { value: 3, translation_key: 'components.requests.bcd.protection_requirements_items.level_1' },
        { value: 4, translation_key: 'components.requests.bcd.protection_requirements_items.level_2' },
        { value: 5, translation_key: 'components.requests.bcd.protection_requirements_items.quarantine_network' },
        { value: 10, translation_key: 'components.requests.bcd.protection_requirements_items.special' }
      ],
      access_to_internet_value: false,
      access_method: null,
      permission_group: '',
      device_count: '1',
      dhcp_pool_wish: false,
      dhcp_pool_size: '2',
      dhcp_own_server: false,
      dhcp_own_server_address: null,
      dhcp_wish: false,
      additional_comments_text: null,
    }
  },
  computed: {
    checkBCDName() {
      return this.bcd_name.trim().length > 0 && this.bcd_name.trim().length <= 23
    },
    translated_protection_requirements() {
      return this.translateOptions(this.protection_requirements_options)
    },
  },
  methods: {
    async update() {
      await this.updateOEs()
    },
    async updateOEs() {
      this.clearOE()
      this.loading++
      const response = await OUService.listOwn(this.$store.state)
      this.oes = response.data.unit_list_superset
      if (this.oes.length === 1) {
        this.selected_oe = this.oes[0]
        this.oe_search = this.serialize_oe(this.selected_oe)
        await this.updateGroups()
      }
      this.loading--
    },
    async updateGroups() {
      this.clearGroup()
      if (!this.selected_oe) return
      this.loading++
      const response = await OUService.getOU(this.$store.state, this.selected_oe.fq_name.toUpperCase())
      this.groups = response.data.group_list
      if (this.groups.length === 1) {
        this.selected_group = this.groups[0]
        this.group_search = this.serialize_group(this.selected_group)
      }
      this.loading--
    },
    clear() {
      this.clearOE()
      this.clearGroup()
      this.clearBCDName()
      this.clearProtectionRequirements()
      this.clearAccessToInternet()
      this.clearAccessMethod()
      this.clearPermissionGroup()
      this.clearDeviceCount()
      this.clearDHCP()
      this.clearAdditionalComment()
      this.additional_recipients = null
      // Clear the error message
      this.error_message = null
      // Clear icon
      this.status_icon = null
    },
    clearOE() {
      this.oes = null
      this.selected_oe = null
      this.oe_search = ''
    },
    clearGroup() {
      this.group_search = null
      this.selected_group = null
      this.group_search = ''
    },
    clearBCDName() {
      this.bcd_name = ''
    },
    clearProtectionRequirements() {
      this.protection_requirements_select = null
      this.protection_requirements_explanation_text = null
    },
    clearAccessToInternet() {
      this.access_from_internet_value = false
    },
    clearAccessMethod() {
      this.access_method = null
    },
    clearPermissionGroup() {
      this.permission_group = ''
    },
    clearDeviceCount() {
      this.device_count = '1'
    },
    clearDHCP() {
      this.dhcp_wish = false
      this.dhcp_own_server = false
      this.dhcp_own_server_address = null
      this.dhcp_pool_wish = false
      this.dhcp_pool_size = '2'
    },
    clearAdditionalComment() {
      this.additional_comments_text = null
    },
    reset() {
      this.clear()
      this.update()
    },
    translateOptions(items) {
      // TODO: Remove this code duplication
      // Iterate over the list of options
      const copy_items = JSON.parse(JSON.stringify(items))
      for (const copy_element of copy_items) {
        // Check if the item is an object to apply translations to
        if (typeof copy_element === 'object') {
          if (Object.hasOwn(copy_element, 'translation_key')) {
            if (Object.hasOwn(copy_element, 'translation_args')) {
              // Generate the text of the item dynamically
              copy_element.text = this.$t(copy_element.translation_key, copy_element.translation_args)
            } else {
              // Generate the text of the item dynamically
              copy_element.text = this.$t(copy_element.translation_key)
            }
          }
        }
      }
      return copy_items
    },
    sendWIFIBCDRequest(e) {
      e.preventDefault()
      this.status_icon = null
      this.sending = true
      this.error_message = null
      let additional_recipients_list = []
      if (this.additional_recipients && this.additional_recipients.trim().length > 0) {
        additional_recipients_list = this.additional_recipients.trim().split(',')
      }
      RequestsService.send_wifi_vlan_request(this.$store.state, {
        oe: this.selected_oe.fq_name,
        group: this.selected_group.name,
        protection_requirement: this.protection_requirements_select,
        protection_requirement_note: this.protection_requirements_explanation_text,
        bcd_name: this.bcd_name,
        access_to_internet: this.access_to_internet_value,
        access_method: this.access_method,
        permission_group: this.permission_group,
        device_count: this.device_count,
        dhcp: this.dhcp_wish,
        dhcp_pool: this.dhcp_pool_wish,
        dhcp_pool_size: this.dhcp_pool_size,
        own_dhcp_server: this.dhcp_own_server,
        own_dhcp_ip: this.dhcp_own_server_address,
        additional_recipients: additional_recipients_list,
        additional_comments: this.additional_comments_text
      }).then(response => {
        this.sending = false
        this.status_icon = 'request_sent'
        this.clear()
        // close modal after 1 second
        setTimeout(() => {
          this.$bvModal.hide('wlan-bcd-request-form')
        }, 1000)
      }).catch(error => {
        window.console.log(error)
        this.sending = false
        this.status_icon = 'transaction_error'
        if (error.response.status >= 400 && error.response.status < 500) {
          this.error_message = error.response.data.detail
        }
      })
    }
  }
}
</script>

<style scoped>

</style>
